import {
  BarChart3,
  BookOpen,
  ClipboardList,
  Database,
  FileBarChart,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PlusCircle,
  School,
  Settings,
  Sun,
  Users,
  X,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useMemo, useState } from "react";
import { useCBTStore } from "../../store/cbtStore";
import Breadcrumbs from "./Breadcrumbs.jsx";

const guruMenu = [
  { key: "guru-dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "manage-ujian", label: "Manajemen Ujian", icon: ClipboardList },
  { key: "create-ujian", label: "Create Ujian", icon: PlusCircle },
  { key: "hasil-ujian-guru", label: "Hasil Ujian", icon: FileBarChart },
  { key: "analisis-ujian", label: "Analisis Ujian", icon: BarChart3 },
];

const adminMenu = [
  { key: "admin-dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "manage-siswa", label: "Manajemen Siswa", icon: Users },
  { key: "manage-guru", label: "Manajemen Guru", icon: GraduationCap },
  { key: "bank-soal", label: "Bank Soal", icon: BookOpen },
  { key: "manage-kelas", label: "Manajemen Kelas", icon: School },
  { key: "laporan-sistem", label: "Laporan Sistem", icon: Database },
];

export function getAdminMenu(role) {
  return role === "ADMIN" ? adminMenu : guruMenu;
}

function Sidebar({ role, currentPage, onNavigate, onClose }) {
  const logout = useCBTStore((state) => state.logout);
  const adminUser = useCBTStore((state) => state.adminUser);
  const theme = useCBTStore((state) => state.theme);
  const setTheme = useCBTStore((state) => state.setTheme);
  const menu = getAdminMenu(role);

  return (
    <aside className="flex h-full w-72 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
      <div className="flex min-h-16 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
        <button
          type="button"
          onClick={() => onNavigate(role === "ADMIN" ? "admin-dashboard" : "guru-dashboard")}
          className="rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600"
        >
          <span className="block text-base font-extrabold text-primary-600 dark:text-blue-300">
            CBT {role === "ADMIN" ? "Admin" : "Guru"}
          </span>
          <span className="block text-xs font-medium text-slate-500 dark:text-slate-400">
            SD Negeri 3 Pringgabaya
          </span>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-2 text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:text-slate-300 dark:hover:bg-slate-800 lg:hidden"
          aria-label="Tutup navigasi"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {role === "ADMIN" ? "Super Admin" : "Guru"}
        </p>
        <p className="mt-1 truncate text-sm font-bold text-slate-900 dark:text-white">{adminUser?.nama}</p>
        <p className="text-xs text-slate-600 dark:text-slate-400">{adminUser?.email}</p>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4" aria-label="Navigasi admin">
        {menu.map((item) => {
          const Icon = item.icon;
          const active = currentPage === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onNavigate(item.key)}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-semibold transition ${
                active
                  ? "bg-primary-50 text-primary-600 dark:bg-primary-600/20 dark:text-blue-200"
                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
              } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600`}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="space-y-2 border-t border-slate-200 p-3 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          {theme === "dark" ? "Light Mode" : "Dark Mode"}
        </button>
        <button
          type="button"
          onClick={() => {
            logout();
            onNavigate("login");
          }}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-error-600 hover:bg-error-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-error-600 dark:hover:bg-error-600/10"
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
          Logout
        </button>
      </div>
    </aside>
  );
}

export default function AdminLayout({ role, currentPage, onNavigate, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const adminUser = useCBTStore((state) => state.adminUser);
  const menu = getAdminMenu(role);

  const currentLabel = useMemo(
    () => menu.find((item) => item.key === currentPage)?.label ?? "Dashboard",
    [currentPage, menu],
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-100">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40" />
          </Transition.Child>
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel>
                <Sidebar
                  role={role}
                  currentPage={currentPage}
                  onNavigate={(page) => {
                    onNavigate(page);
                    setSidebarOpen(false);
                  }}
                  onClose={() => setSidebarOpen(false)}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <div className="hidden lg:fixed lg:inset-y-0 lg:flex">
        <Sidebar role={role} currentPage={currentPage} onNavigate={onNavigate} onClose={() => undefined} />
      </div>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
          <div className="flex min-h-16 items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="rounded-md p-2 text-slate-700 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:text-slate-200 dark:hover:bg-slate-800 lg:hidden"
                aria-label="Buka navigasi"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <Breadcrumbs items={[currentLabel]} />
                <h1 className="mt-1 text-base font-extrabold text-slate-950 dark:text-white sm:text-lg">
                  {currentLabel}
                </h1>
              </div>
            </div>
            <div className="hidden items-center gap-3 sm:flex">
              <button
                type="button"
                className="rounded-md border border-slate-300 p-2 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                aria-label="Pengaturan"
              >
                <Settings className="h-5 w-5" />
              </button>
              <div className="text-right">
                <p className="text-sm font-bold text-slate-900 dark:text-white">{adminUser?.nama}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{role}</p>
              </div>
            </div>
          </div>
        </header>

        <main id="main-content" className="px-4 py-5 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
}
